import { useSyncExternalStore } from 'react'

/**
 * État partagé de la messagerie Intercom, dont le lanceur par défaut est masqué : un bouton
 * flottant à nous l'ouvre (`SupportChatButton`), et sa pastille lit les non-lus remontés par
 * `IntercomProvider`. L'utilisateur déplace ce bouton ; sa position est propre au navigateur, et
 * la fenêtre s'ouvre du côté de l'écran où il est posé. À gauche, elle se décale de la largeur de
 * la sidenav, que celle-ci publie tant qu'elle est affichée.
 */
export type IntercomAlignment = 'left' | 'right'

/** Distances du bouton aux bords droit et bas de la fenêtre, en px. */
export type SupportChatButtonPosition = { right: number; bottom: number }

export const SUPPORT_CHAT_BUTTON_SIZE_PX = 40
/** Écart entre le bouton et le bord de l'écran contre lequel il est posé. */
export const SUPPORT_CHAT_BUTTON_MARGIN_PX = 16
export const SUPPORT_CHAT_BUTTON_POSITION_STORAGE_KEY =
  'nabu.supportChat.position'

const DEFAULT_POSITION: SupportChatButtonPosition = {
  right: SUPPORT_CHAT_BUTTON_MARGIN_PX,
  bottom: SUPPORT_CHAT_BUTTON_MARGIN_PX,
}

const readPosition = (): SupportChatButtonPosition => {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(SUPPORT_CHAT_BUTTON_POSITION_STORAGE_KEY) ??
        'null'
    )
    if (
      parsed &&
      typeof parsed === 'object' &&
      Number.isFinite((parsed as SupportChatButtonPosition).right) &&
      Number.isFinite((parsed as SupportChatButtonPosition).bottom)
    ) {
      const { right, bottom } = parsed as SupportChatButtonPosition
      return { right, bottom }
    }
  } catch {
    // position absente ou illisible : coin bas droit
  }
  return DEFAULT_POSITION
}

/**
 * Garde le bouton contre le bord droit ou le bord bas de l'écran, jamais au milieu, en haut ni à
 * gauche : il est collé au plus proche des deux et ne glisse que le long de celui-ci. Le long du
 * bas, il s'arrête avant la sidenav (`viewport.width` est la largeur à sa droite). Reste valable
 * après un redimensionnement.
 */
export const clampSupportChatButtonPosition = (
  position: SupportChatButtonPosition,
  viewport: { width: number; height: number }
): SupportChatButtonPosition => {
  const max = (extent: number) =>
    Math.max(
      SUPPORT_CHAT_BUTTON_MARGIN_PX,
      extent - SUPPORT_CHAT_BUTTON_SIZE_PX - SUPPORT_CHAT_BUTTON_MARGIN_PX
    )
  const clamp = (value: number, upper: number) =>
    Math.round(Math.min(Math.max(value, SUPPORT_CHAT_BUTTON_MARGIN_PX), upper))
  const right = clamp(position.right, max(viewport.width))
  const bottom = clamp(position.bottom, max(viewport.height))

  // À égalité (dans le coin), le bord droit l'emporte.
  return right <= bottom
    ? { right: SUPPORT_CHAT_BUTTON_MARGIN_PX, bottom }
    : { right, bottom: SUPPORT_CHAT_BUTTON_MARGIN_PX }
}

/** La fenêtre s'ouvre du côté de l'écran où le bouton est posé. */
export const alignmentForPosition = (
  position: SupportChatButtonPosition,
  viewportWidth: number
): IntercomAlignment =>
  position.right + SUPPORT_CHAT_BUTTON_SIZE_PX / 2 > viewportWidth / 2
    ? 'left'
    : 'right'

let position = readPosition()
let unreadCount = 0
let sideNavWidth = 0
const listeners = new Set<() => void>()

const emit = () => {
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getSupportChatButtonPosition = () => position

/** Le côté de la fenêtre dépend de la largeur de l'écran : à relire après un redimensionnement. */
export const refreshSupportChatLayout = () => emit()

/** `persist: false` pendant le glisser : on n'écrit qu'au lâcher. */
export const setSupportChatButtonPosition = (
  next: SupportChatButtonPosition,
  { persist = true }: { persist?: boolean } = {}
) => {
  if (persist) {
    try {
      window.localStorage.setItem(
        SUPPORT_CHAT_BUTTON_POSITION_STORAGE_KEY,
        JSON.stringify(next)
      )
    } catch {
      // ignore quota / private mode
    }
  }
  if (next.right === position.right && next.bottom === position.bottom) return
  position = next
  emit()
}

export const useSupportChatButtonPosition = () =>
  useSyncExternalStore(
    subscribe,
    getSupportChatButtonPosition,
    getSupportChatButtonPosition
  )

export const getIntercomAlignment = (): IntercomAlignment =>
  alignmentForPosition(position, window.innerWidth)

export const useIntercomAlignment = () =>
  useSyncExternalStore(subscribe, getIntercomAlignment, getIntercomAlignment)

export const setIntercomUnreadCount = (count: number) => {
  if (count === unreadCount) return
  unreadCount = count
  emit()
}

const getUnreadCount = () => unreadCount

export const useIntercomUnreadCount = () =>
  useSyncExternalStore(subscribe, getUnreadCount, getUnreadCount)

/** 0 quand aucune sidenav n'est affichée (mobile, écrans hors dashboard). */
export const setIntercomSideNavWidth = (width: number) => {
  if (width === sideNavWidth) return
  sideNavWidth = width
  emit()
}

export const getIntercomSideNavWidth = () => sideNavWidth

export const useIntercomSideNavWidth = () =>
  useSyncExternalStore(
    subscribe,
    getIntercomSideNavWidth,
    getIntercomSideNavWidth
  )
